/**
 * Carregamento do índice e revalidação — o mesmo ciclo do radar.
 *
 * Só baixa quando a camada é ligada. Depois, revalida:
 * - a cada ciclo da atualização automática (gancho do loop);
 * - quando a janela do loop anda (chegou imagem nova do satélite) — é nessa
 *   hora que o quadro novo precisa da fumaça nova.
 * O índice tem ~10 KB comprimido e a máquina responde 304 quando nada mudou.
 */
import { subscribeSelector } from '../lib/store';
import { useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { BASE_FUMACA, carregarIndice, type IndiceFumaca } from './api';
import { useSmokeStore } from './store';
import { avisoDeFrescor } from './tempo';

/** Revalidações a menos disto do último pedido são ignoradas. */
const INTERVALO_MIN_MS = 55_000;

let emVoo: AbortController | null = null;
let jaCarregou = false;
let ultimoPedido = 0;
/** A camada registra aqui o "tente de novo os que falharam". */
let tentarDeNovo: (() => void) | null = null;

export function registrarNovaTentativa(fn: (() => void) | null): void {
  tentarDeNovo = fn;
}

function mesmoIndice(a: IndiceFumaca | null, b: IndiceFumaca): boolean {
  if (!a || a.quadros.length !== b.quadros.length) return false;
  return a.quadros.every((q, i) => q.c === b.quadros[i].c && q.n === b.quadros[i].n);
}

export async function carregarFumaca(force = false): Promise<void> {
  if (!BASE_FUMACA) return;
  const { situacao } = useSmokeStore.getState();
  if (!force && (situacao.estado === 'loading' || jaCarregou)) return;
  emVoo?.abort();
  const ctrl = new AbortController();
  emVoo = ctrl;
  ultimoPedido = Date.now();
  // A revalidação é silenciosa: "Baixando…" piscando a cada ciclo é ruído.
  if (!jaCarregou) useSmokeStore.setState({ situacao: { estado: 'loading', erro: null } });
  try {
    const indice = await carregarIndice(ctrl.signal);
    if (ctrl.signal.aborted) return;
    jaCarregou = true;
    // Mesmos quadros: não trocar poupa replanejar a camada.
    const atual = useSmokeStore.getState().indice;
    useSmokeStore.setState({
      indice: mesmoIndice(atual, indice) ? { ...atual!, gerado: indice.gerado } : indice,
      situacao: { estado: 'ready', erro: null },
    });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    // Revalidação que falhou com dado na tela: mantém o dado e mostra o erro.
    useSmokeStore.setState({ situacao: { estado: 'error', erro: e instanceof Error ? e.message : String(e) } });
  } finally {
    if (emVoo === ctrl) emVoo = null;
  }
}

/** Revalidação do ciclo automático — só com a camada em uso. */
export function refreshFumaca(): void {
  if (!useSmokeStore.getState().visivel) return;
  tentarDeNovo?.();
  const nuncaCarregou = !jaCarregou && useSmokeStore.getState().situacao.estado === 'error';
  if (!jaCarregou && !nuncaCarregou) return;
  if (Date.now() - ultimoPedido < INTERVALO_MIN_MS) return;
  void carregarFumaca(true);
}

export function iniciarSyncFumaca(): () => void {
  if (useSmokeStore.getState().visivel) void carregarFumaca();
  const aoVoltarConexao = () => {
    ultimoPedido = 0;
    refreshFumaca();
  };
  if (typeof window !== 'undefined') window.addEventListener('online', aoVoltarConexao);
  const unsubs = [
    useSmokeStore.subscribe((a, b) => {
      if (!a.visivel || b.visivel) return;
      // Religar depois de um tempo: o índice guardado pode ser de horas atrás.
      if (!jaCarregou) void carregarFumaca();
      else if (Date.now() - ultimoPedido >= INTERVALO_MIN_MS) void carregarFumaca(true);
    }),
    subscribeSelector(useTimeStore, (s) => s.range?.end ?? null, refreshFumaca),
  ];
  return () => {
    unsubs.forEach((u) => u());
    if (typeof window !== 'undefined') window.removeEventListener('online', aoVoltarConexao);
  };
}

/** Instante do quadro mais novo publicado, ou null. */
export function ultimoQuadro(indice: IndiceFumaca | null): number | null {
  const q = indice?.quadros;
  return q?.length ? q[q.length - 1].t : null;
}

/** Ponto amarelo no trilho: camada ligada e dado com problema. */
export function useFumacaDesatualizada(): boolean {
  const visivel = useSmokeStore((s) => s.visivel);
  const erro = useSmokeStore((s) => s.situacao.estado === 'error' && !s.indice);
  const ultimo = useSmokeStore((s) => ultimoQuadro(s.indice));
  // Relógio próprio: com a máquina parada o índice não muda.
  const agora = useNow(60_000);
  if (!visivel) return false;
  return erro || avisoDeFrescor(ultimo, agora) !== null;
}

/** Só para os testes. */
export function resetSmokeSync(): void {
  emVoo?.abort();
  emVoo = null;
  jaCarregou = false;
  ultimoPedido = 0;
}
